import {authManager} from '@/components/Auth/AuthManager.ts';
import {apiEndpoint} from '@/utils/Api.ts';
import BackIcon from '@mui/icons-material/ArrowBack';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import Alert from '@mui/lab/Alert';
import AlertTitle from '@mui/lab/AlertTitle';
import {Box, useTheme} from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Slider from '@mui/material/Slider';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography/Typography';
import {useSnackbar} from 'notistack';
import type * as React from 'react';
import {useCallback, useState} from 'react';
import Cropper, {type Area} from 'react-easy-crop';
import InlineLoadingIndicator from '../../components/InlineLoadingIndicator';
import PaddedContainer from '../../components/PaddedContainer';

type Props = {
    open: boolean;
    onClose: () => void;
};

const cropPicture = async (source: string, crop: Area) => {
    const image = new Image();
    image.src = source;

    await new Promise((resolve, reject) => {
        image.addEventListener('load', resolve);
        image.addEventListener('error', reject);
    });

    const canvas = document.createElement('canvas');
    canvas.width = 400;
    canvas.height = 600;

    const context = canvas.getContext('2d');
    context?.drawImage(image, crop.x, crop.y, crop.width, crop.height, 0, 0, 400, 600);

    return canvas.toDataURL('image/jpeg', 100).replace(/^data:image\/jpeg;base64,/, '');
};

const SubmitPhotoDialog: React.FC<Props> = ({open, onClose}: Props) => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [imageSource, setImageSource] = useState<string | null>(null);
    const [crop, setCrop] = useState({x: 0, y: 0});
    const [zoom, setZoom] = useState(1);
    const [finalCrop, setFinalCrop] = useState<Area | null>(null);
    const {enqueueSnackbar} = useSnackbar();
    const [showConfirm, setShowConfirm] = useState(false);

    const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLoading(true);
        const reader = new FileReader();

        reader.addEventListener('load', event => {
            setCrop({x: 0, y: 0});
            setZoom(1);
            setImageSource(event.target?.result as string);
            setLoading(false);
        });

        if (event.target.files?.[0]) {
            reader.readAsDataURL(event.target.files[0]);
        }
    };

    const handleCropComplete = useCallback((_croppedArea: Area, croppedAreaPixels: Area) => {
        setFinalCrop(croppedAreaPixels);
    }, []);

    const handleSubmit = useCallback(async () => {
        if (!(imageSource && finalCrop)) {
            return;
        }

        setLoading(true);
        const data = await cropPicture(imageSource, finalCrop);
        setImageSource(null);

        const accessToken = authManager.getAccessToken();

        if (!accessToken) {
            enqueueSnackbar('Your authentication expired, please try again.', {variant: 'error'});
            setLoading(false);
            onClose();
            return;
        }

        let response: Response;

        try {
            response = await fetch(new URL('/photo', apiEndpoint).toString(), {
                method: 'POST',
                body: JSON.stringify({photo: data}),
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
            });
        } catch (e) {
            enqueueSnackbar('Server not reachable, please try again later.', {variant: 'error'});
            setLoading(false);
            onClose();
            return;
        }

        setLoading(false);

        if (!response.ok) {
            if (response.status === 401) {
                enqueueSnackbar('Your authentication expired, please try again.', {
                    variant: 'error',
                });
                onClose();
                return;
            }

            if (response.status === 409) {
                const error = (await response.json()) as {hint: string};
                let errorMessage: string | null = null;

                switch (error.hint) {
                    case 'photo-pending-validation':
                        errorMessage = 'You already have a photo pending validation.';
                        break;

                    case 'photo-recently-approved':
                        errorMessage = 'Photo was approved within the last 180 days.';
                        break;
                }

                if (errorMessage) {
                    enqueueSnackbar(errorMessage, {variant: 'error'});
                    onClose();
                    return;
                }
            }

            enqueueSnackbar('An unknown error occurred.', {variant: 'error'});
            onClose();
            return;
        }

        enqueueSnackbar('Photo has been submitted.', {variant: 'success'});
        onClose();
    }, [imageSource, finalCrop, onClose, enqueueSnackbar]);

    const guidelines = (
        <ul
            style={{
                paddingLeft: '1.2em',
            }}
        >
            <li>The photo should be a headshot of the individual, not a group or family photo.</li>
            <li>The photo should have a solid, neutral-colored background.</li>
            <li>The photo should be color, with no filters applied.</li>
            <li>The individual should face forward, with head and shoulders visible.</li>
            <li>The individual's eyes should be open and visible.</li>
            <li>The individual should smile or have a straight face.</li>
            <li>
                The individual should have their hands and arms at their side and not visible (not a
                selfie).
            </li>
            <li>The individual should not be wearing a hat or bandana, etc.</li>
            <li>There should be no pets or animals in the photo.</li>
            <li>The photo on file is still valid; update denied at this time.</li>
        </ul>
    );

    return (
        <Dialog open={open} fullScreen>
            <AppBar position='static'>
                <Toolbar>
                    <IconButton edge='start' color='inherit' onClick={onClose}>
                        <BackIcon />
                    </IconButton>
                    <Typography variant='h6'>Submit new photo</Typography>
                </Toolbar>
            </AppBar>

            <PaddedContainer>
                {loading ? (
                    <InlineLoadingIndicator />
                ) : imageSource ? (
                    <>
                        <Box
                            sx={{
                                position: 'relative',
                                width: '100%',
                                maxWidth: '100%',
                                height: 300,
                            }}
                        >
                            <Cropper
                                aspect={2 / 3}
                                showGrid={false}
                                zoomSpeed={2}
                                minZoom={1}
                                maxZoom={3}
                                image={imageSource}
                                onCropChange={setCrop}
                                onCropComplete={handleCropComplete}
                                crop={crop}
                                onZoomChange={setZoom}
                                zoom={zoom}
                            />
                        </Box>
                        <Box
                            sx={{
                                padding: theme.spacing(0, 2),
                            }}
                        >
                            <Slider
                                min={1}
                                max={3}
                                value={zoom}
                                step={0.01}
                                onChange={(_event, value) => {
                                    setZoom(value as number);
                                }}
                            />
                        </Box>

                        <Button
                            color='secondary'
                            variant='contained'
                            onClick={() => {
                                setShowConfirm(true);
                            }}
                        >
                            Submit
                        </Button>
                    </>
                ) : (
                    <>
                        <Box
                            sx={{
                                textAlign: 'center',
                            }}
                        >
                            <Button
                                startIcon={<PhotoCameraIcon />}
                                variant='contained'
                                color='secondary'
                                component='label'
                            >
                                Select Photo
                                <input
                                    type='file'
                                    accept='image/*'
                                    onChange={handleImageSelect}
                                    style={{display: 'none'}}
                                />
                            </Button>
                        </Box>

                        <Alert
                            severity='info'
                            sx={{
                                marginTop: theme.spacing(2),
                            }}
                        >
                            <AlertTitle>
                                Photos submitted should resemble a yearbook photo and adhere to the
                                following guidelines:
                            </AlertTitle>
                        </Alert>

                        {guidelines}
                    </>
                )}
            </PaddedContainer>

            <Dialog
                open={showConfirm}
                fullWidth
                onClose={(_event, reason) => {
                    if (reason === 'backdropClick') {
                        return;
                    }

                    setShowConfirm(false);
                }}
            >
                <DialogTitle>Does your photo meet the guidelines?</DialogTitle>
                <DialogContent dividers>{guidelines}</DialogContent>
                <DialogActions>
                    <Button
                        autoFocus
                        onClick={() => {
                            setShowConfirm(false);
                        }}
                        color='primary'
                    >
                        No
                    </Button>
                    <Button
                        onClick={() => {
                            setShowConfirm(false);
                            void handleSubmit();
                        }}
                        color='primary'
                        autoFocus
                    >
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
};

export default SubmitPhotoDialog;
